import { useRef, useCallback } from "react";

import { useUnmount } from "app/custom-hooks/useUnmount";
import type { ChatThreadModel } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/lead-generator/types/models";

import { useChatScrolling } from "./useChatScrolling";

type HookParams = Pick<ChatThreadModel, "sendMessage"> & {
    scrollBottomOffset?: number;
};

type HookValue = {
    containerRef: React.RefObject<HTMLDivElement | null>;
    bottomRef: React.RefObject<HTMLDivElement | null>;
    lastUserMessageRef: React.RefObject<HTMLDivElement | null>;
    lastAiMessageRef: React.RefObject<HTMLDivElement | null>;
    onMessageSubmit: (input: string) => void;
    onResponseComplete: VoidFunction;
};

export const useChatScrollEffects = ({ sendMessage, scrollBottomOffset }: HookParams): HookValue => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const bottomRef = useRef<HTMLDivElement | null>(null);
    const lastUserMessageRef = useRef<HTMLDivElement | null>(null);
    const lastAiMessageRef = useRef<HTMLDivElement | null>(null);

    const rafRef = useRef<number | null>(null);

    const { scrollChatWhenAiResponse, scrollUserMessageToTop } = useChatScrolling({
        containerRef,
        lastAiMessageRef,
        lastUserMessageRef,
        bottomRef,
    });

    const handleMessageSubmit = useCallback(
        (input: string): void => {
            const result = sendMessage(input);

            if (result.success) {
                rafRef.current = requestAnimationFrame(() => scrollUserMessageToTop(scrollBottomOffset));
            }
        },
        [sendMessage, scrollUserMessageToTop, scrollBottomOffset],
    );

    const handleResponseComplete = useCallback(() => {
        rafRef.current = requestAnimationFrame(() => scrollChatWhenAiResponse());
    }, [scrollChatWhenAiResponse]);

    useUnmount(() => {
        if (rafRef.current !== null) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
    });

    return {
        containerRef,
        bottomRef,
        lastUserMessageRef,
        lastAiMessageRef,
        onMessageSubmit: handleMessageSubmit,
        onResponseComplete: handleResponseComplete,
    };
};
